"use client";

import * as PopoverPrimitive from "@radix-ui/react-popover";
import type { Transition } from "motion/react";
import React from "react";
import Animate from "@/components/animations/Animate";
import { breakpointClasses, cn, splitBreakpointProps } from "@/lib/utils";
import { getInitialPosition } from "../helpers";
import type { PopoverContentProps } from "../types";
import { usePopover } from "./popover-root";

const DEFAULT_TRANSITION: Transition = {
  type: "spring",
  stiffness: 300,
  damping: 25,
};

function PopoverContent({
  align = "center",
  side = "bottom",
  sideOffset = 4,
  transition = DEFAULT_TRANSITION,
  className,
  children,
  ...props
}: PopoverContentProps) {
  const { isOpen, getContentHoverProps } = usePopover();
  const initialPosition = getInitialPosition(side);
  const { bp, native } = splitBreakpointProps(props);
  const classes = breakpointClasses(bp);

  return (
    <Animate.Presence>
      {isOpen && (
        <PopoverPrimitive.Portal forceMount data-slot="popover-portal">
          <PopoverPrimitive.Content
            forceMount
            align={align}
            sideOffset={sideOffset}
            onOpenAutoFocus={(e: Event) => e.preventDefault()}
            onCloseAutoFocus={(e: Event) => e.preventDefault()}
            {...native}
          >
            <Animate
              key="popover-content"
              data-slot="popover-content"
              initial={{ opacity: 0, scale: 0.5, ...initialPosition }}
              animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
              exit={{ opacity: 0, scale: 0.5, ...initialPosition }}
              transition={transition}
              className={cn(
                "w-72 rounded-lg border bg-popover p-4 text-popover-foreground shadow-md outline-none",
                className,
                classes,
              )}
              {...getContentHoverProps()}
              {...native}
            >
              {children}
            </Animate>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      )}
    </Animate.Presence>
  );
}

PopoverContent.displayName = "Popover.Content";

export default PopoverContent;
